import { Avatar, Col, Text, useTheme } from "@rally/ui-kit";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { failureSaid } from "../../localization/failure.ts";
import { roleLabel, thinkingLabel } from "../../localization/labels.ts";
import { say } from "../../localization/say.ts";
import { drawnToneFor } from "../../me/a-drawn-face.ts";
import { faceOfAnAgent } from "../../me/an-agents-mark.ts";
import { calledBy } from "../../me/called.ts";
import { useFace } from "../../me/face.ts";
import { RenameAgent } from "../../me/rename-agent.tsx";
import { act, read } from "../../net/rally.ts";
import { type Logged, type Turn, turnInFlight } from "../activity.ts";
import { coalesced, LatestRequest } from "../activity-refresh.ts";
import { Composer, type SubmissionIdentity } from "../composer/composer.tsx";
import { canonicalTypedPayload } from "../message/message-payload.ts";
import { usePanelActions } from "../panel/panels.tsx";
import type { Card } from "../panel/stacking.ts";
import type { PersonHere } from "../person/person.tsx";
import { useLive } from "./live.ts";
import { useNow } from "./tool-frame.tsx";
import { TurnDetail } from "./turn-detail.tsx";
import { turnWithSteps } from "./turn-inspector-model.ts";
import { elapsedSaid, type Someone, stateOf, turnOf } from "./working.ts";

type Agent = {
  account: string;
  handle: string;
  model: string;
  thinking: string;
  tools: boolean;
  owner: string;
};

type Busy = { thinking: Someone[]; typing: Someone[]; unanswered: Someone[] };

type Activity = { turns: Turn[]; steps: Logged[] };

const A_BEAT = 3000;

const A_FEED_IS = 30;

export function agentCard(conversation: string, me: string, who: PersonHere): Card {
  return {
    key: `agent:${who.account}`,
    title: calledBy(who) ?? who.handle,
    render: () => <Agent conversation={conversation} me={me} who={who} />,
  };
}

function Caption({ children }: { children: string }) {
  return (
    <Text typeName="caption" color="text.tertiary">
      {children}
    </Text>
  );
}

export function Agent({
  conversation,
  me,
  who,
}: {
  conversation: string;
  me: string;

  who: PersonHere;
}) {
  const theme = useTheme();
  const [here, setHere] = useState<PersonHere[] | null>(null);
  const [agents, setAgents] = useState<Agent[] | null>(null);
  const [busy, setBusy] = useState<Busy | null>(null);
  const [activity, setActivity] = useState<Activity | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [naming, setNaming] = useState(false);
  const [working, setWorking] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const activityReads = useRef(new LatestRequest()).current;
  const busyReads = useRef(new LatestRequest()).current;

  const refreshActivity = useCallback(
    () =>
      activityReads.run(
        () => read<Activity>("agent.activity", { conversation, account: who.account }),
        setActivity,
        () => setActivity(null),
      ),
    [activityReads, conversation, who.account],
  );
  const telemetryRefresh = useMemo(() => coalesced(refreshActivity), [refreshActivity]);

  const again = useCallback(() => {
    void read<PersonHere[]>("conversation.people", { conversation })
      .then(setHere)
      .catch(() => undefined);
    void read<Agent[]>("my.agents")
      .then(setAgents)
      .catch(() => setAgents([]));
    telemetryRefresh.notice();

    void read<{ role: string | null }>("me")
      .then((mine) => setRole(mine.role))
      .catch(() => setRole(null));
  }, [conversation, telemetryRefresh]);
  useEffect(() => {
    again();
    return () => {
      activityReads.invalidate();
      busyReads.invalidate();
    };
  }, [activityReads, again, busyReads]);

  useEffect(() => {
    const ask = () => {
      busyReads.run(
        () => read<Busy>("conversation.busy", { conversation }),
        setBusy,
        () => setBusy(null),
      );
    };
    ask();
    const timer = setInterval(ask, A_BEAT);
    return () => clearInterval(timer);
  }, [busyReads, conversation]);

  useEffect(() => () => telemetryRefresh.cancel(), [telemetryRefresh]);
  useLive((notice) => {
    if (notice.conversation !== conversation) return;
    if (notice.kind.startsWith("run.")) telemetryRefresh.notice();
    else again();
  }, "everything");

  const them = here?.find((one) => one.account === who.account) ?? who;
  const agent = agents?.find((one) => one.account === who.account) ?? null;
  const called = calledBy(them) ?? them.handle;
  const face = useFace(them.avatar);
  const state = stateOf(who.account, busy);
  const turn = turnOf(who.account, busy);
  const flight = turnInFlight(activity);
  const now = useNow(state === "thinking" || flight !== null);

  const clock = elapsedSaid(turn?.startedAt ?? flight?.startedAt, now);

  const mayRename = agent !== null && (agent.owner === me || role === "owner" || role === "admin");

  const doing = async (what: () => Promise<unknown>): Promise<boolean> => {
    setWorking(true);
    try {
      await what();
      setSaid(null);
      return true;
    } catch (cause) {
      setSaid(failureSaid(cause, say("agent.broke")));
      return false;
    } finally {
      setWorking(false);
    }
  };

  const halt = () =>
    doing(async () => {
      if (turn?.run) await act("run.halt", { run: turn.run });
      setBusy(null);
      again();
    });

  const write = (text: string, identify: SubmissionIdentity) =>
    doing(async () => {
      const said = `@${them.handle} ${text}`;
      const submission = identify(canonicalTypedPayload({ conversation, text: said }));
      await act("message.post", { conversation, text: said, submission });
      again();
    });

  usePanelActions([
    ...(mayRename
      ? [
          {
            key: "rename",
            icon: "pencil" as const,
            label: say("agent.rename"),
            testID: "agent-rename",
            disabled: working,
            onPress: () => setNaming(true),
          },
        ]
      : []),
    ...(turn?.run
      ? [
          {
            key: "stop",
            icon: "stop" as const,
            label: say("agent.stop"),
            testID: "agent-stop",
            disabled: working,
            onPress: () => void halt(),
          },
        ]
      : []),
  ]);

  return (
    <Col style={{ gap: theme.space[4] }}>
      <Col style={{ flexDirection: "row", alignItems: "center", gap: theme.space[3] }}>
        <Avatar
          name={called}
          src={face}
          size={48}
          {...faceOfAnAgent(agent?.model ?? null)}
          tone={drawnToneFor(them.handle)}
        />
        <Text
          typeName="caption"
          color="text.tertiary"
          numberOfLines={1}
          testID="agent-name"
          style={{ flex: 1, minWidth: 0 }}
        >
          {[
            `@${them.handle}`,
            state === "thinking"
              ? say("agents.thinkingNow")
              : state === "waiting"
                ? say("agents.owed")
                : say("agents.idle"),
            ...(clock ? [clock] : []),
            roleLabel(them.role),
            ...(agent
              ? [
                  say("agent.spec")(
                    agent.model,
                    thinkingLabel(agent.thinking),
                    agent.tools ? say("agent.toolsOn") : say("agent.toolsOff"),
                  ),
                ]
              : []),
          ].join(" · ")}
        </Text>
      </Col>

      <Col style={{ gap: theme.space[2] }} testID="agent-activity">
        <Caption>{say("agent.activity")}</Caption>
        {(activity?.turns ?? []).length === 0 ? (
          <Text typeName="caption" color="text.tertiary" testID="agent-nothing-yet">
            {say("agent.nothingYet")}
          </Text>
        ) : (
          (activity?.turns ?? []).slice(0, A_FEED_IS).map((one) => {
            const requester = here?.find((person) => person.account === one.request?.by);
            const requestedBy = requester ? (calledBy(requester) ?? requester.handle) : null;
            return (
              <TurnDetail
                key={one.run}
                turn={turnWithSteps(one, activity?.steps ?? [])}
                requestedBy={requestedBy}
              />
            );
          })
        )}
      </Col>

      <Col
        style={{
          gap: theme.space[1],
          position: "sticky" as "relative",
          bottom: 0,
          backgroundColor: theme.color["bg.surface"],
          paddingTop: theme.space[2],
        }}
        testID="agent-fixed-composer"
      >
        <Composer
          conversation={conversation}
          draftKey={`${conversation}:agent:${them.account}`}
          placeholder={say("agent.write")(them.handle)}
          testID="agent-write"
          busy={working}
          error={said}
          onSay={(text, _from, identify) => write(text, identify)}
        />
        <Text typeName="caption" color="text.tertiary">
          {say("agent.writeMeans")}
        </Text>
      </Col>

      <RenameAgent
        account={who.account}
        called={called}
        name={them.displayName ?? null}
        visible={naming}
        onDismiss={() => setNaming(false)}
        onRenamed={again}
      />
    </Col>
  );
}
